import { UzDropdownMenu, type UzDropdownMenuItem } from '@utazzo/ui';
import { Check, Filter, Search, X } from 'lucide-react';
import React from 'react';

import type { DataGridFilter, DataGridFilterOption } from './types';

import { cn } from '@/lib/cn';

/**
 * Above this many options a chip gets a search box unless the filter says
 * otherwise. Fixed enums (statuses, approval states) stay below it; lists that
 * grow with the data - cities, countries, team members - cross it as soon as
 * they are worth searching.
 */
export const SEARCHABLE_OPTION_THRESHOLD = 12;

/** Shared by the chip and the card-grid toolbars' select, so one filter never
 *  gains a search box in one view and loses it in the other. */
export const isSearchableFilter = (filter: DataGridFilter): boolean =>
  filter.searchable ?? filter.options.length > SEARCHABLE_OPTION_THRESHOLD;

/** Case-insensitive substring match on the label; '' matches everything. */
const matchOptions = (
  options: ReadonlyArray<DataGridFilterOption>,
  query: string,
): ReadonlyArray<DataGridFilterOption> => {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.label.toLowerCase().includes(q));
};

export interface DataGridFilterChipProps {
  filter: DataGridFilter;
  /** Current value ('' = no filter applied). Multi mode stores a CSV string. */
  value: string;
  onChange: (value: string) => void;
}

// Kept tight on purpose: a catalog list carries 7-8 chips plus the search box
// and the toolbar actions, and every extra pixel of padding is what pushes the
// row onto a second line.
const triggerClass = (active: boolean): string =>
  cn(
    'inline-flex h-[34px] max-w-[13rem] items-center gap-1 rounded-md px-2 text-[13px] transition-colors',
    'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
    active
      ? 'border border-solid border-[var(--border-strong)] text-[var(--fg)]'
      : 'border border-dashed border-[var(--border-strong)] text-[var(--fg-muted)] hover:border-[var(--z-400)]',
  );

/** Truncated: a long value ("United Arab Emirates") must not stretch the chip
 *  wide enough to wrap the toolbar. */
const valuePill =
  'max-w-[7rem] truncate rounded-[4px] bg-[var(--z-100)] px-1.5 py-px text-[12px] font-medium text-[var(--fg)]';

/**
 * Faceted filter trigger styled as a dashed "chip" that turns solid and
 * appends a value pill when active. Replaces the old labelled <UzSelect>.
 *
 * Single mode (default) uses a Radix dropdown that closes on pick. Multi mode
 * (`filter.multi`) renders a custom checkbox popover - Radix dropdowns close on
 * every item select, which fights multi-select - and stores the chosen values
 * as a comma-separated string.
 *
 * Searchable single-select gets its own popover too: a Radix dropdown owns
 * every keystroke for its typeahead, so a text input nested inside it never
 * receives what the operator types.
 */
export const DataGridFilterChip = ({
  filter,
  value,
  onChange,
}: DataGridFilterChipProps): React.JSX.Element => {
  if (filter.multi) {
    return <MultiFilterChip filter={filter} value={value} onChange={onChange} />;
  }
  if (isSearchableFilter(filter)) {
    return <SearchableFilterChip filter={filter} value={value} onChange={onChange} />;
  }

  const active = value !== '' && value !== null && value !== undefined;
  const current = filter.options.find((o) => o.value === value);

  const items: UzDropdownMenuItem[] = [
    { type: 'label', label: filter.label },
    ...filter.options.map(
      (opt): UzDropdownMenuItem => ({
        type: 'item',
        label: opt.label,
        icon: (
          <Check
            className="h-3.5 w-3.5"
            aria-hidden
            style={{ visibility: value === opt.value ? 'visible' : 'hidden' }}
          />
        ),
        onSelect: () => onChange(opt.value),
      }),
    ),
    ...(active
      ? ([
          { type: 'separator' },
          {
            type: 'item',
            label: 'Clear',
            icon: <X className="h-3.5 w-3.5" aria-hidden />,
            onSelect: () => onChange(''),
          },
        ] as UzDropdownMenuItem[])
      : []),
  ];

  return (
    <UzDropdownMenu
      align="start"
      // Long option lists (e.g. ~200 countries) must scroll inside the menu
      // rather than overflow the viewport.
      contentClassName="max-h-[18rem] overflow-y-auto"
      trigger={
        <button type="button" className={triggerClass(active)}>
          <Filter className="h-3 w-3" aria-hidden />
          <span>{filter.label}</span>
          {active && (
            <>
              <span className="h-3.5 w-px bg-[var(--border)]" aria-hidden />
              <span className={valuePill}>{current?.label ?? value}</span>
            </>
          )}
        </button>
      }
      items={items}
    />
  );
};

/** Popover shell shared by the two custom (non-Radix) chip variants. */
const popoverClass =
  'absolute top-full left-0 z-50 mt-1.5 flex max-h-[20rem] w-[15rem] flex-col rounded-md border border-[var(--ds-border)] bg-[var(--bg)] p-1 shadow-md';

const headingClass =
  'px-2 py-1.5 text-[12px] font-medium tracking-wide text-[var(--fg-muted)] uppercase';

const optionRowClass =
  'flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[14px] text-[var(--fg)] hover:bg-[var(--z-100)]';

/** Search field pinned above the option list. Autofocused: the operator opened
 *  this chip to find something, so typing should just work. */
const FilterSearchBox = ({
  value,
  onChange,
  onEnter,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  onEnter: () => void;
  label: string;
}): React.JSX.Element => (
  <div className="mb-1 flex items-center gap-1.5 border-b border-[var(--ds-border)] px-2 pb-1.5">
    <Search className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" aria-hidden />
    <input
      // eslint-disable-next-line jsx-a11y/no-autofocus -- the popover exists to be typed into
      autoFocus
      type="text"
      value={value}
      aria-label={`Search ${label}`}
      placeholder="Search…"
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnter();
        }
      }}
      className="h-7 w-full bg-transparent text-[14px] text-[var(--fg)] outline-none placeholder:text-[var(--fg-muted)]"
    />
  </div>
);

const NoMatches = (): React.JSX.Element => (
  <p className="px-2 py-3 text-center text-[13px] text-[var(--fg-muted)]">No matches</p>
);

/**
 * Searchable single-select variant: same chip, but the popover leads with a
 * search box. For lists that grow with the data - every city the catalog
 * touches, every country, every team member - scrolling is not a filter.
 *
 * Not a Radix dropdown (unlike the plain single-select above): that component
 * routes keystrokes into its own typeahead, so an input nested in it never
 * sees them. Click-away + Escape close it, Enter takes the first match.
 */
const SearchableFilterChip = ({
  filter,
  value,
  onChange,
}: DataGridFilterChipProps): React.JSX.Element => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const active = value !== '' && value !== null && value !== undefined;
  const current = filter.options.find((o) => o.value === value);
  const visible = React.useMemo(() => matchOptions(filter.options, query), [filter.options, query]);

  const pick = (next: string): void => {
    onChange(next);
    setOpen(false);
  };
  // A stale query would silently hide options the next time it opens.
  const toggle = (): void => {
    setQuery('');
    setOpen((o) => !o);
  };

  return (
    <div className="relative">
      <button type="button" className={triggerClass(active)} onClick={toggle}>
        <Filter className="h-3 w-3" aria-hidden />
        <span>{filter.label}</span>
        {active && (
          <>
            <span className="h-3.5 w-px bg-[var(--border)]" aria-hidden />
            <span className={valuePill}>{current?.label ?? value}</span>
          </>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
            className={popoverClass}
          >
            <div className={headingClass}>{filter.label}</div>
            <FilterSearchBox
              value={query}
              onChange={setQuery}
              onEnter={() => visible[0] && pick(visible[0].value)}
              label={filter.label}
            />
            <div className="min-h-0 flex-1 overflow-y-auto">
              {visible.length === 0 ? (
                <NoMatches />
              ) : (
                visible.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={value === opt.value}
                    onClick={() => pick(opt.value)}
                    className={optionRowClass}
                  >
                    <Check
                      className="h-3.5 w-3.5 shrink-0"
                      aria-hidden
                      style={{ visibility: value === opt.value ? 'visible' : 'hidden' }}
                    />
                    <span className="truncate">{opt.label}</span>
                  </button>
                ))
              )}
            </div>
            {active && (
              <>
                <div className="my-1 h-px bg-[var(--ds-border)]" />
                <button
                  type="button"
                  onClick={() => pick('')}
                  className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[14px] text-[var(--fg-muted)] hover:bg-[var(--z-100)]"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                  Clear
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Multi-select variant: a click-away popover of checkboxes. Selected values are
 * joined into the CSV string the URL state carries. The synthetic "all" option
 * (`value: ''`) is filtered out - clearing every box is the empty state.
 */
const MultiFilterChip = ({
  filter,
  value,
  onChange,
}: DataGridFilterChipProps): React.JSX.Element => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const options = React.useMemo(
    () => filter.options.filter((o) => o.value !== ''),
    [filter.options],
  );
  const selected = React.useMemo(() => (value ? value.split(',').filter(Boolean) : []), [value]);
  const active = selected.length > 0;
  // Same rule as the single-select chip: search once the list is long enough
  // to scroll. Ticking boxes leaves the popover open, so the query survives
  // across picks by design - you filter once, then tick several.
  const searchable = isSearchableFilter(filter);
  const visible = React.useMemo(
    () => (searchable ? matchOptions(options, query) : options),
    [searchable, options, query],
  );

  const toggle = (optValue: string): void => {
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onChange(next.join(','));
  };

  const summary =
    selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selected`;

  return (
    <div className="relative">
      <button
        type="button"
        className={triggerClass(active)}
        onClick={() => {
          setQuery('');
          setOpen((o) => !o);
        }}
      >
        <Filter className="h-3 w-3" aria-hidden />
        <span>{filter.label}</span>
        {active && (
          <>
            <span className="h-3.5 w-px bg-[var(--border)]" aria-hidden />
            <span className={valuePill}>{summary}</span>
          </>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          {/* Capped and scrollable for the same reason the single-select
              variant is (see contentClassName above): option lists here run
              long - roles alone are ~15 - and an uncapped popover runs off the
              bottom of the viewport with no way to reach the last options.
              Only the option list scrolls; the heading and Clear stay pinned. */}
          <div
            role="menu"
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
            className="absolute top-full left-0 z-50 mt-1.5 flex max-h-[20rem] min-w-[200px] flex-col rounded-md border border-[var(--ds-border)] bg-[var(--bg)] p-1 shadow-md"
          >
            <div className={headingClass}>{filter.label}</div>
            {searchable && (
              <FilterSearchBox
                value={query}
                onChange={setQuery}
                // Enter ticks the first match instead of closing - the popover
                // is a multi-select, so it stays open either way.
                onEnter={() => visible[0] && toggle(visible[0].value)}
                label={filter.label}
              />
            )}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {searchable && visible.length === 0 && <NoMatches />}
              {visible.map((opt) => {
                const checked = selected.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={checked}
                    onClick={() => toggle(opt.value)}
                    className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[14px] text-[var(--fg)] hover:bg-[var(--z-100)]"
                  >
                    <span
                      className={cn(
                        'grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border',
                        checked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-[var(--border-strong)]',
                      )}
                    >
                      {checked && <Check className="h-3 w-3" aria-hidden />}
                    </span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
            {active && (
              <>
                <div className="my-1 h-px bg-[var(--ds-border)]" />
                <button
                  type="button"
                  onClick={() => onChange('')}
                  className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[14px] text-[var(--fg-muted)] hover:bg-[var(--z-100)]"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                  Clear
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
